<template>
  <div class="d-flex flex-column main-container-canvas">
    <!-- Top-nav and filtering section -->
    <!-- Hide if the user expands the canvas to fullscreen -->
    <v-row v-if="!fullScreenExpanded" class="flex-grow-0 ma-0 toolbar-row">
      <v-col cols="3" class="px-0">
        <div class="d-flex align-end">
          <!-- Tile / List view toggle -->
          <v-btn-toggle
            class="mr-2"
            mandatory
            :model-value="selectedView"
            @update:model-value="updateSelectedView"
          >
            <v-btn :value="views.Tile">
              {{ tkey(views.Tile) }}
            </v-btn>

            <v-btn :value="views.List">
              {{ tkey(views.List) }}
            </v-btn>
          </v-btn-toggle>
          <span class="docs-link">
            <DocsLink link="toolguide/120-assortment.html" />
          </span>

          <h4 v-if="selectedCanvas.outdated" class="ml-2 warning-banner">
            {{ t('assortmentCanvasesPanel.errorMessages.outdatedWarning') }}
          </h4>
        </div>
      </v-col>

      <v-col class="px-0 text-center">
        <span v-if="cannGroupColumnSettingsEnabled && isDisplayByCDT" class="pr-2">
          <!-- This column settings panel allows to select and reorder cann groups to display -->
          <!-- It's only present if the feature flag is enabled and if the user is on CDT view -->
          <ColumnSettings
            :button-title="tkey('cannGroups')"
            :columns="canvasCannGroupColumns"
            @update:selected-columns="toggleCanvasCannGroupVisibility"
            @update:column-order="reorderCanvasCannGroupColumns"
          />
        </span>
        <span v-if="isAlternativeCanvasDisplaysEnabled">
          <!-- Display by filter toggle -->
          <span class="pr-2">{{ tkey('displayBy.title') }}:</span>
          <v-btn-toggle v-model="currentSelectionOfDisplayBy" mandatory>
            <v-btn value="cdt">
              {{ tkey('displayBy.cdt') }}
            </v-btn>
            <v-menu offset="1" max-height="400">
              <template #activator="{ props }">
                <v-btn :value="displayByMenu" class="pr-1" v-bind="props">
                  {{ t('general.filter') }}
                  {{
                    tkey(`displayBy.${displayByMenu}`, { fallback: getDisplayByMenuFallbackName })
                  }}
                  <span class="pl-2">|</span>
                  <v-icon
                    :color="currentSelectionOfDisplayBy !== 'cdt' ? 'background' : 'primary'"
                    size="24"
                  >
                    mdi-chevron-down
                  </v-icon>
                </v-btn>
              </template>
              <!-- List of options consisting of config enabled options + custom attrs -->
              <v-list>
                <v-list-item
                  v-for="(item, index) in displayByMenuItems"
                  :key="index"
                  class="display-by-title"
                  @click="selectDisplayByMenuItem(item)"
                >
                  {{ tkey(`displayBy.${item}`) }}
                </v-list-item>

                <!-- custom attributes -->
                <template v-if="showCustomAttributesInDisplayByMenu">
                  <v-divider />
                  <v-list-item
                    v-for="item in getScenarioAttributes"
                    :key="item.id"
                    class="display-by-title"
                    @click="selectDisplayByMenuItem(item.id)"
                  >
                    {{ item.name }}
                  </v-list-item>
                </template>
              </v-list>
            </v-menu>
          </v-btn-toggle>
        </span>
      </v-col>

      <v-col v-if="showNotImplemented" cols="2" class="px-0 row">
        <!-- Disabled in scope of AOV3-829 -->
        <v-slider :model-value="50" min="0" max="100" hide-details class="w-25 mw-25" disabled>
          <template #append>
            <v-btn icon color="primary" disabled>
              <v-icon>zoom_out_map</v-icon>
            </v-btn>
          </template>
        </v-slider>
      </v-col>

      <v-col data-id-e2e="btnsCheckpointsOptimise" class="d-flex justify-end pr-2">
        <div v-if="selectedView === views.Tile" class="d-flex align-center justify-center">
          <h4 class="mr-2">{{ tkey('selectTileSize') }}:</h4>

          <!-- Tile size toggle -->
          <v-btn-toggle
            mandatory
            :model-value="selectedTileSize"
            class="mr-6"
            @update:model-value="setTileSize"
          >
            <v-btn value="medium">
              {{ tkey('medium') }}
            </v-btn>

            <v-btn value="large">
              {{ tkey('large') }}
            </v-btn>
          </v-btn-toggle>
        </div>

        <v-btn color="primary" class="mr-2" @click="displayCheckpoints">
          {{ tkey('checkpoints.title') }}
        </v-btn>

        <v-btn
          action
          :disabled="isAnyJobRunning || isCanvasConfigInvalidForOptimisation || isEditingDisabled"
          @click="isCheckpointCreationEligible"
        >
          {{ t('actions.optimise') }}
        </v-btn>

        <ErrorTriangle
          :arrow="false"
          :style="{ 'margin-bottom': '12px', 'margin-left': '5px' }"
          :errors="{
            [tkey('errorMessages.setOptimiserSettings')]: !isCanvasConfigInvalidForOptimisation,
          }"
        />
      </v-col>
    </v-row>

    <v-row v-if="isCanvasLockedByFinalCheckpoint" class="flex-grow-0 ma-0">
      <v-col cols="12" class="px-0">
        <h4 class="ml-2 warning-banner">
          {{ t('assortmentCanvasesPanel.finalCheckpointLocked.message') }}
        </h4>
      </v-col>
    </v-row>

    <ProgressBar
      v-if="areCanvasProductsDownloading || isAnyJobRunning || renderingCanvas"
      :message="loadingMessage"
      style="margin: initial"
      class="pt-5"
    />

    <template v-if="!areCanvasProductsDownloading && !isAnyJobRunning">
      <CanvasToolbar />

      <LegendFilterHighlight
        v-if="canvasFilterLegendEnabled && showFilterLegend"
        class="grid-container-overflow"
      />

      <!-- This is static across both list and tile view -->
      <SpacebreakSettings
        v-if="spacebreakSettings.open && selectedCanvas.storeClassId"
        :spacebreak="selectedSpacebreak"
        :store-class-id="selectedCanvas.storeClassId"
      />
      <!-- TODO: Vuetify  <ProductPanel style="display: flex;position:absolute;right:0/> -->
      <!-- Wrapper element to provide alternative layout for list view only  -->
      <div :class="[selectedView === views.Tile ? 'd-contents' : 'canvas-wrapper']">
        <!-- Fixed spacebreak column on left hand side -->
        <SpacebreakSidebar
          v-if="selectedView === views.List"
          :points-of-distribution="pointsOfDistribution"
        />
        <!-- Classes are applied conditionally to provide different scrolling behaviours for each view -->
        <!-- The canvas grid and sidebar should scroll independantly for list view only -->
        <div
          ma-0
          pa-0
          :class="[
            selectedView === views.Tile ? 'infinite-canvas flex-grow-1' : 'grid-container-wrapper',
          ]"
        >
          <div :style="gridContainerOverflow" class="grid-container-overflow">
            <!-- This renders a spacebreak sidebar and a column per cann group -->
            <!-- The distinction between list and tile views happens lower in the component tree, on cdt-column -->
            <CanvasGrid
              v-if="orderedScenarioCdts"
              :key="orderedScenarioCdtsKey"
              :ordered-scenario-cdts="orderedScenarioCdts"
            />
          </div>
        </div>
      </div>
    </template>

    <!-- dialog to display list of checkpoints -->
    <v-dialog
      v-model="isCheckpointListModalOpen"
      :width="'76%'"
      :min-width="960"
      @keydown.esc="closeCheckpoints"
      @click:outside="closeCheckpoints"
    >
      <BaseDialogCard
        class="checkpoints-dialog"
        :border="true"
        :title="tkey('checkpoints.title')"
        @close="closeCheckpoints"
      >
        <ScenarioCheckpointsList @close="isCheckpointListModalOpen = false" />
      </BaseDialogCard>
    </v-dialog>

    <!-- pre-optimisation checkpoint creation confirmation dialog -->
    <v-dialog v-model="isConfirmationCheckpointCreationModalOpen" max-width="300">
      <BaseDialogCard
        class="checkpoint-creation-dialog"
        :title="t('assortmentCanvasPage.checkpointModal.title')"
        @close="close"
      >
        <v-card-text class="text-h6 pl-0">
          <p>{{ tkey('checkpointModal.text') }}</p>
        </v-card-text>
        <template #footer>
          <v-row>
            <v-col class="d-flex justify-end">
              <v-btn
                data-id-e2e="btnAgreeCheckpoint"
                action
                @click="
                  [initOptimisation(true), (isConfirmationCheckpointCreationModalOpen = false)]
                "
              >
                {{ tkey('checkpointModal.agree') }}
              </v-btn>
              <v-btn
                data-id-e2e="btnDisagreeCheckpoint"
                class="ml-2"
                @click="
                  [initOptimisation(false), (isConfirmationCheckpointCreationModalOpen = false)]
                "
              >
                {{ tkey('checkpointModal.disagree') }}
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </BaseDialogCard>
    </v-dialog>
    <ProductDashboard />
  </div>
</template>

<script>
import { getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';
import { each, filter, find, get, includes, isEmpty, keyBy, sortBy } from 'lodash';
import { mapActions, mapState } from 'pinia';

// enums
import assortmentCanvases from '@enums/assortment-canvases';
import jobapi from '@enums/jobapi';
import scenarioAssortments from '@enums/scenario-assortments';

// helpers and utils
import { jobApi } from '@/helpers';

// composables
import { useInternationalization } from '@/composables/use-internationalization';
import { useCanvasEditability } from '@/composables/use-canvas-editability';
import { useFeature } from '@/composables/use-feature';
import { useAiAgentActions } from '@/composables/use-ai-agent-actions';

// stores
import { useAiStore } from '@/stores/ai';
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import { useClusteringStore } from '@/stores/clustering';
import { useContextStore } from '@/stores/context';
import { useFurnitureStore } from '@/stores/furniture';
import { useScenarioCdtsStore } from '@/stores/scenario-cdts';
import { useScenarioProductsStore } from '@/stores/scenario-products';
import { useScenariosStore } from '@/stores/scenarios';
import { useWorkpackagesStore } from '@/stores/workpackages';

export default {
  setup() {
    const instance = getCurrentInstance();
    const router = useRouter();
    const { canvasCanBeEdited } = useCanvasEditability();
    const { cannGroupColumnSettingsEnabled, canvasFilterLegendEnabled, aiPanelEnabled } =
      useFeature();
    const { tkey, t } = useInternationalization('assortmentCanvasPage');

    useAiAgentActions({
      allowScenarioWorkflowNavigation: true,

      getExclude: () => {
        const excluded = [];
        const noCanvas = !instance.proxy.selectedCanvas._id;
        if (
          noCanvas ||
          instance.proxy.isAnyJobRunning ||
          instance.proxy.isEditingDisabled ||
          instance.proxy.isCanvasConfigInvalidForOptimisation
        ) {
          excluded.push('run-optimiser');
        }
        if (noCanvas) {
          excluded.push(
            'apply-filter',
            'clear-filters',
            'fetch-product-details',
            'switch-view',
            'switch-canvas',
            'open-product-dashboard'
          );
        }
        if (instance.proxy.currentAiSection === 'assortment-product-dashboard') {
          excluded.push('open-product-dashboard');
        }
        return [...new Set(excluded)];
      },

      getSupplementary: () => ({
        'switch-canvas': instance.proxy.buildAvailableCanvasesSupplementary(),
      }),

      actions: {
        'switch-view': {
          description: 'Switch the canvas between tile view and list view.',
          readOnly: true,
          parameters: {
            type: 'object',
            properties: {
              view: {
                type: 'string',
                enum: ['tile', 'list'],
                description:
                  'The target view. Use "tile" for the visual tile layout, "list" for the ranked KPI table.',
              },
            },
            required: ['view'],
          },
          handler: async ({ view }) => {
            if (instance.proxy.selectedView === view) {
              return `[Tool] Action succeeded: already in ${view} view.`;
            }
            await instance.proxy.updateSelectedView(view);
            return `[Tool] Action succeeded: switched to ${view} view.`;
          },
        },

        'switch-canvas': {
          description:
            'Switch the active assortment canvas to a different cluster / store class combination. Use the supplementary context to map a canvas label to its id.',
          readOnly: true,
          parameters: {
            type: 'object',
            properties: {
              canvasId: {
                type: 'string',
                description: 'The _id of the canvas to switch to.',
              },
            },
            required: ['canvasId'],
          },
          handler: ({ canvasId }) => {
            const canvas = (instance.proxy.canvases || []).find(c => c._id === canvasId);
            if (!canvas) {
              return `[Tool] Action failed: no canvas found with id "${canvasId}". Check the available canvases list.`;
            }
            if (instance.proxy.selectedCanvas._id === canvasId) {
              return '[Tool] Action succeeded: already on this canvas.';
            }
            instance.proxy.setIsProductSidebarOpen(false);
            instance.proxy.setDashboardProduct(null);
            instance.proxy.closeSpacebreakSettingsPanel();
            router.push({
              name: instance.proxy.$route.name,
              params: { ...instance.proxy.$route.params, canvasId },
              query: instance.proxy.$route.query,
            });
            const label = `${canvas.clusterName || 'Unclustered'} - ${canvas.storeClassName || 'Unknown'}`;
            return `[Tool] Action succeeded: switching to canvas "${label}".`;
          },
        },

        'apply-filter': {
          description:
            'Filter the canvas products by a custom attribute value. Products not matching will be hidden.',
          readOnly: true,
          parameters: {
            type: 'object',
            properties: {
              attribute: {
                type: 'string',
                description: 'The human-readable attribute name to filter by (e.g. "Brand").',
              },
              values: {
                type: 'array',
                items: { type: 'string' },
                description: 'The attribute value(s) to include. Case-insensitive.',
              },
            },
            required: ['attribute', 'values'],
          },
          handler: ({ attribute, values }) => {
            if (!attribute || !values || values.length === 0) {
              return '[Tool] Action failed: attribute and values are required.';
            }
            const attrs = instance.proxy.getScenarioAttributes || [];
            const attr = attrs.find(a => a.name.toLowerCase() === attribute.toLowerCase());
            if (!attr) {
              const attrNames = attrs.map(a => a.name).join(', ') || 'none available';
              return `[Tool] Action failed: attribute "${attribute}" not found. Available: ${attrNames}`;
            }
            const available = instance.proxy.availableProductAttributesValues(attr.id);
            const matchedValues = values.flatMap(v => {
              const match = available.find(
                a => String(a.name).toLowerCase() === String(v).toLowerCase()
              );
              return match ? [match] : [];
            });
            if (matchedValues.length === 0) {
              const availableNames = available.map(a => a.name).join(', ');
              return `[Tool] Action failed: none of the provided values match available values for "${attr.name}". Available: ${availableNames}`;
            }
            const newFilter = {
              operator: assortmentCanvases.FilterOperators.Equal,
              filterType: assortmentCanvases.Filters.customAttributes,
              attributeId: attr.id,
              filterName: attr.name,
              filterValues: matchedValues,
            };
            // Replace any existing filter for the same attribute, then append new one
            const existingFilters = instance.proxy.canvasProductsFilters || [];
            const updatedFilters = [
              ...existingFilters.filter(
                f =>
                  !(
                    f.filterType === assortmentCanvases.Filters.customAttributes &&
                    f.attributeId === attr.id
                  )
              ),
              newFilter,
            ];
            instance.proxy.setCanvasProductsFilters(updatedFilters);
            instance.proxy.setCanvasProductsFiltersSelection(updatedFilters);
            return `[Tool] Action succeeded: filtered by ${attr.name} = ${matchedValues.map(v => v.name).join(', ')}.`;
          },
        },

        'clear-filters': {
          description: 'Remove all active canvas product filters and show all products.',
          readOnly: true,
          parameters: { type: 'object', properties: {}, required: [] },
          handler: () => {
            instance.proxy.setCanvasProductsFilters([]);
            instance.proxy.setCanvasProductsFiltersSelection([]);
            return '[Tool] Action succeeded: all filters cleared.';
          },
        },

        'run-optimiser': {
          description:
            'Start the assortment optimiser for the currently active canvas. This re-ranks all products. A checkpoint creation confirmation may appear — this is expected behaviour.',
          parameters: { type: 'object', properties: {}, required: [] },
          confirmationLabel: 'run the assortment optimiser for the current canvas',
          handler: async () => {
            if (instance.proxy.isAnyJobRunning) {
              return '[Tool] Action failed: an optimisation job is already running. Wait for it to complete before starting another.';
            }
            if (instance.proxy.isCanvasConfigInvalidForOptimisation) {
              return '[Tool] Action failed: the optimiser settings are not fully configured. Check the optimiser strategy settings for this scenario.';
            }
            if (instance.proxy.isEditingDisabled) {
              return '[Tool] Action failed: you do not have permission to edit this canvas.';
            }
            if (instance.proxy.isConfirmationCheckpointCreationModalOpen) {
              return '[Tool] Action failed: a checkpoint confirmation dialog is already open. Please respond to it first.';
            }
            await instance.proxy.isCheckpointCreationEligible();
            return '[Tool] Action succeeded: optimisation started. The canvas will refresh when complete.';
          },
        },

        'fetch-product-details': {
          description:
            'Look up detailed KPI and placement information for one or more products by name from the current canvas. Useful for comparing products or explaining rank differences. Max 10 products.',
          readOnly: true,
          parameters: {
            type: 'object',
            properties: {
              productNames: {
                type: 'array',
                items: { type: 'string' },
                description: 'Product names or product key displays to look up. Case-insensitive.',
              },
            },
            required: ['productNames'],
          },
          handler: ({ productNames }) => {
            if (!productNames || productNames.length === 0) {
              return '[Tool] Action failed: productNames is required.';
            }
            const products = instance.proxy.canvasProducts || [];
            const spacebreaks = instance.proxy.spacebreaks || [];
            const found = productNames.slice(0, 10).flatMap(name => {
              const lname = name.toLowerCase();
              const match = products.find(
                p =>
                  (p.itemDescription || '').toLowerCase() === lname ||
                  (p.productKeyDisplay || '').toLowerCase() === lname
              );
              if (!match) return [];
              const currentSb = spacebreaks.find(sb => sb._id === match.currentSpacebreakId);
              const optimisedSb = spacebreaks.find(sb => sb._id === match.optimisedSpacebreakId);
              const tags = [];
              if (match.isNew) tags.push('NEW — no history, fully modelled');
              else if (match.isModelled) tags.push('MDL — limited data, modelled');
              else if (match.isNewInCluster) tags.push('NIC — new in this cluster');
              return [
                {
                  name: match.itemDescription || match.productKeyDisplay,
                  productKey: match.productKeyDisplay || match.productKey,
                  tag: tags[0] || null,
                  currentSpacebreak: currentSb?.name || match.currentSpacebreakId || 'Not listed',
                  optimisedSpacebreak:
                    optimisedSb?.name || match.optimisedSpacebreakId || 'Not listed',
                  lockState: match.lockType || null,
                  rank: match.optimisedRank ?? null,
                  sales: match.sales ?? null,
                  volume: match.volume ?? null,
                  margin: match.margin ?? null,
                  selfSwitching: match.selfSwitching ?? null,
                  content: match.contentValue
                    ? `${match.contentValue} ${match.contentUnitOfMeasure || ''}`.trim()
                    : null,
                  optimiserNotes: match.optimisedNote || null,
                },
              ];
            });
            const notFound = productNames.filter(name => {
              const lname = name.toLowerCase();
              return !products.some(
                p =>
                  (p.itemDescription || '').toLowerCase() === lname ||
                  (p.productKeyDisplay || '').toLowerCase() === lname
              );
            });
            if (found.length === 0) {
              return `[Tool] No products found matching: ${productNames.join(', ')}. Use exact product names as shown on the canvas.`;
            }
            return JSON.stringify(
              { found, notFound: notFound.length > 0 ? notFound : undefined },
              null,
              2
            );
          },
        },

        'open-product-dashboard': {
          description:
            'Open the full product dashboard for a specific product to show switching partners, transferred spend, and distribution data. Only one product dashboard can be open at a time — calling this replaces any currently open dashboard.',
          readOnly: true,
          parameters: {
            type: 'object',
            properties: {
              productName: {
                type: 'string',
                description:
                  'The name or product key display of the product to open. Case-insensitive.',
              },
            },
            required: ['productName'],
          },
          handler: ({ productName }) => {
            if (instance.proxy.spacebreakSettings.open) {
              return '[Tool] Action failed: the constraints panel is currently open. Close it first before opening the product dashboard.';
            }
            const products = instance.proxy.canvasProducts || [];
            const lname = productName.toLowerCase();
            const match = products.find(
              p =>
                (p.itemDescription || '').toLowerCase() === lname ||
                (p.productKeyDisplay || '').toLowerCase() === lname
            );
            if (!match) {
              return `[Tool] Action failed: product "${productName}" not found on the current canvas.`;
            }
            instance.proxy.setDashboardProduct(match);
            instance.proxy.setShowDashboard(true);
            const displayName = match.itemDescription || match.productKeyDisplay;
            return `[Tool] Action succeeded: product dashboard opened for "${displayName}". Switching partner and transferred spend data are loading.`;
          },
        },
      },
    });

    return {
      canvasCanBeEdited,
      cannGroupColumnSettingsEnabled,
      canvasFilterLegendEnabled,
      aiPanelEnabled,
      tkey,
      t,
    };
  },

  data() {
    return {
      canvasId: this.$route.params.canvasId,
      cdtCols: [],
      cannGroupColumns: [],
      headers: scenarioAssortments.TABLE_HEADERS.map(item => ({
        key: item.key,
        text: this.tkey(`tableHeaders.${item.key}`),
        value: item.value,
      })),
      renderingCanvas: false,
      views: assortmentCanvases.Views,
      showLegend: true,
      displayByMenu: '',
      isCheckpointListModalOpen: false,
      isConfirmationCheckpointCreationModalOpen: false,
    };
  },

  computed: {
    ...mapState(useAssortmentCanvasStore, [
      'spacebreaks',
      'referenceCheckpoint',
      'selectedReferenceCheckpoint',
      'spacebreakSettings',
      'selectedTileSize',
      'selectedView',
      'fullScreenExpanded',
      'unifiedColumns',
      'showFilterLegend',
      'getPointsOfDistribution',
      'selectedCanvas',
      'canvases',
      'canvasProducts',
      'selectedPod',
      'isCanvasConfigInvalidForOptimisation',
      'displayBy',
      'canvasCannGroupColumns',
      'visibleCanvasCannGroups',
      'checkpoints',
      'getDashboardProduct',
      'getShowDashboard',
      'productsPerSpacebreak',
      'spaceUsedPerSpacebreak',
      'isProductSidebarOpen',
      'openedProductTooltips',
      'canvasProductsFilters',
      'getSelectedProductTopPartners',
      'getSelectedProductTransferredSpend',
      'availableProductAttributesValues',
      'isCanvasLockedByFinalCheckpoint',
    ]),
    ...mapState(useFurnitureStore, ['scenarioFurniture']),
    ...mapState(useScenarioCdtsStore, ['scenarioCdts']),
    ...mapState(useScenariosStore, ['isJobRunning', 'selectedScenario']),
    ...mapState(useWorkpackagesStore, ['selectedWorkpackage']),
    ...mapState(useContextStore, ['clientConfig', 'getClientConfig', 'showNotImplemented']),
    ...mapState(useScenarioProductsStore, ['scenarioProducts']),
    ...mapState(useClusteringStore, ['getScenarioAttributes']),

    pointsOfDistribution() {
      // Points of distribution are calculated across all storeclasses so we select the one for this canvas.
      return get(this, 'selectedPod', {});
    },

    isAssortmentOptimisationJobRunning() {
      return this.optimizerJobStatus
        ? !includes(jobapi.jobFinishedStatuses, this.optimizerJobStatus)
        : false;
    },

    isClScSwitchingJobRunning() {
      return this.isJobRunning('clScSwitchingMatricesCalculation');
    },

    isAnyJobRunning() {
      return this.isAssortmentOptimisationJobRunning || this.isClScSwitchingJobRunning;
    },

    optimizerJobStatus() {
      const canvas = this.canvases.find(c => c._id === this.selectedCanvas._id);
      return get(canvas, 'jobs.assortmentOptimisation.status', '').toLowerCase();
    },

    formattedProductsData() {
      return this.canvasProducts.map(item =>
        Object.fromEntries(
          // convert to array, map, and then fromEntries gives back the object
          Object.entries(item).map(([key, value]) => {
            return [key, value];
          })
        )
      );
    },
    selectedSpacebreak() {
      return this.spacebreakSettings.spacebreak;
    },

    // Order visible CDTs by the order of their cann groups
    // Order visible CDTs by the order of their cann groups
    orderedScenarioCdts() {
      if (!this.visibleCanvasCannGroups) return this.scenarioCdts;

      // Create lookup object for faster access
      const visibleCannGroupsByKey = keyBy(this.visibleCanvasCannGroups, 'key');

      // Map CDTs with their cann group orders
      const cdtsWithCannGroups = this.scenarioCdts.reduce((acc, cdt) => {
        const cannGroup = visibleCannGroupsByKey[cdt.cannGroupId];

        if (cannGroup) {
          acc.push({
            ...cdt,
            order: cannGroup.order,
          });
        }
        return acc;
      }, []);

      // Sort by order
      return sortBy(cdtsWithCannGroups, 'order');
    },

    orderedScenarioCdtsKey() {
      return this.orderedScenarioCdts.map(cdt => cdt._id).join('-');
    },

    areCanvasProductsDownloading() {
      return this.formattedProductsData.length === 0;
    },

    loadingMessage() {
      if (this.isAssortmentOptimisationJobRunning) {
        return this.t('messages.inProgress');
      }
      if (this.areCanvasProductsDownloading) {
        return this.t('messages.canvasProductsLoadingInProgress');
      }
      return this.t('messages.productsRendering');
    },

    selectedCanvasId() {
      return this.selectedCanvas._id;
    },

    gridContainerOverflow() {
      if (this.unifiedColumns[this.selectedView]) return { width: '100%' };
      return { width: 'fit-content' };
    },

    isEditingDisabled() {
      return !this.canvasCanBeEdited;
    },

    // TODO move to useFeature
    isAlternativeCanvasDisplaysEnabled() {
      return get(
        this.getClientConfig,
        'features.scenario.assortmentCanvas.alternativeCanvasDisplaysEnabled',
        false
      );
    },

    showInactiveProducts() {
      return get(this.getClientConfig, 'canvas.showInactiveProducts', true);
    },

    // TODO move to useConfig (create a useConfig composable)
    displayByMenuItems() {
      return get(this.getClientConfig, 'assortmentCanvas.displayByMenuOptions', []);
    },

    showCustomAttributesInDisplayByMenu() {
      return get(
        this.getClientConfig,
        'features.scenario.assortmentCanvas.showCustomAttributesInDisplayByMenu',
        false
      );
    },

    scenarioCustomAttributesById() {
      return keyBy(this.getScenarioAttributes, 'id');
    },

    getDisplayByMenuFallbackName() {
      return this.scenarioCustomAttributesById[this.displayByMenu]
        ? this.scenarioCustomAttributesById[this.displayByMenu].name
        : this.displayByMenu;
    },

    currentSelectionOfDisplayBy: {
      get() {
        return this.displayBy;
      },
      set(value) {
        this.setDisplayBy(value);
      },
    },

    isDisplayByCDT() {
      return this.displayBy === assortmentCanvases.DisplayBy.cdt;
    },

    /**
     * Determine the AI section name based on which sub-panel/view is currently active.
     * Priority order: constraints > dashboard > list > tile
     * Note: checkpoints modal and product tooltip no longer have dedicated sections —
     * those contexts are folded into the tile/list and dashboard prompts.
     */
    currentAiSection() {
      if (this.spacebreakSettings.open) return 'assortment-constraints';
      if (this.getShowDashboard) return 'assortment-product-dashboard';
      if (this.selectedView === this.views.List) return 'assortment-list';
      return 'assortment-tile';
    },
  },

  watch: {
    selectedCanvasId: {
      immediate: true,
      async handler() {
        await this.loadCanvas();
        const aiStore = useAiStore();
        aiStore.setSidePanelContextFactory(() => this.buildInnerContext());
        // Signal that canvas data is ready for the agent to act on.
        // This also fires on mid-session canvas switches, which is intentional —
        // the agent should not act until the new canvas data is loaded.
        aiStore.setContextReady(true);
      },
    },

    currentAiSection: {
      immediate: true,
      handler() {
        const aiStore = useAiStore();
        aiStore.updateSidePanelSection(this.currentAiSection);
      },
    },
  },

  mounted() {
    const aiStore = useAiStore();
    aiStore.enableSidePanel({
      section: this.currentAiSection,
      contextDisplay: [this.selectedScenario?.name].filter(Boolean),
    });
  },

  created() {
    // CDT should be selected by default
    this.setDisplayBy(assortmentCanvases.DisplayBy.cdt);
    this.initializeCanvasCannGroupColumns();

    // init default second option
    if (this.isAlternativeCanvasDisplaysEnabled && !isEmpty(this.displayByMenuItems)) {
      this.displayByMenu = this.displayByMenuItems[0];
    }
  },

  unmounted() {
    // Reset the height counter as Vuex data is otherwise preserved and will
    // render the wrong heights on reload.
    // This may need to expand to cover other assortment canvas store data resets in the future.
    this.resetExpandedCdts();
    this.setAssortmentCanvasProducts([]);
    // Reset fullscreen and sticky header settings in vuex when component destroyed
    this.setFullScreen(false);
    // Clear any open product tooltips
    this.clearOpenedProductTooltips();
    // (no AI panel teardown needed — route watcher in AiSidePanel handles context reset)
  },

  methods: {
    ...mapActions(useAssortmentCanvasStore, [
      'setSpacebreaks',
      'setSelectedView',
      'setTileSize',
      'resetExpandedCdts',
      'setSelectedPod',
      'setSelectedCanvasAsOptimised',
      'setAssortmentCanvasProducts',
      'setFullScreen',
      'setStickyHeaders',
      'setUnifiedColumns',
      'setDisplayBy',
      'closeSpacebreakSettingsPanel',
      'setActiveSpacebreak',
      'setIsProductSidebarOpen',
      'setDashboardProduct',
      'clearOpenedProductTooltips',
      'fetchCanvasProducts',
      'fetchCanvases',
      'fetchCanvas',
      'fetchCheckpoints',
      'updateAssortmentCanvas',
      'refreshReferenceCheckpointData',
      'setReferenceCheckpoint',
      'fetchCanvas',
      'changeCanvasRenderingStatus',
      'runAssortmentOptimisation',
      'refreshProductDeltas',
      'initializeListViewColumns',
      'initializeCanvasCannGroupColumns',
      'reorderCanvasCannGroupColumns',
      'toggleCanvasCannGroupVisibility',
      'setCanvasProductsFilters',
      'setCanvasProductsFiltersSelection',
      'setShowDashboard',
    ]),
    ...mapActions(useScenarioCdtsStore, ['setScenarioCdts', 'fetchScenarioCdts']),
    ...mapActions(useScenarioProductsStore, ['setScenarioProducts', 'fetchScenarioProducts']),
    ...mapActions(useFurnitureStore, ['fetchScenarioFurniture']),
    ...mapActions(useClusteringStore, ['fetchScenarioClusters']),

    async loadCanvas() {
      if (!this.selectedCanvas._id) return;
      const canvasId = this.selectedCanvas._id;
      this.setAssortmentCanvasProducts([]);
      this.clearOpenedProductTooltips();
      // show the rendering spinner.
      this.changeCanvasRenderingStatus(true);
      this.setActiveSpacebreak(null);

      // Build params for fetching scenario-products
      const spParams = {};
      if (!this.showInactiveProducts) {
        // If the config flag is off, we only fetch products in assortment
        // TODO instead of fetching scenario products in parallel with canvas products,
        // we should pass the list of product keys to fetchScenarioProducts to avoid fetching unnecessary data.
        // Also, currently both fetchScenarioProducts and fetchCanvasProducts fetch full mongo documents,
        // which is inefficient. We should find which fields are actually needed and only fetch those.
        // Moreover, we might not need to fetch scenario products at all here, see
        // https://github.com/owg-digital/rtls-assortment/blob/master/webtool/server/src/services/assortment-canvases-products.js#L82
        spParams.where = { assortment: true };
      }

      // Step 1: Fetch initial data in parallel
      await Promise.all([
        this.fetchCanvas({ canvasId }),
        this.fetchScenarioFurniture(),
        this.fetchScenarioClusters(),
        this.fetchScenarioCdts({ params: { selected: true } }),
      ]);
      // Step 2: Process CDT columns and refresh checkpoint data.
      // These depend on scenarioCdts and canvas being loaded)
      await Promise.all([this.getCdtCols(), this.refreshReferenceCheckpointData()]);

      if (this.isAssortmentOptimisationJobRunning) {
        return;
      }
      // Step 3: Set spacebreaks (depends on canvas and furniture data)
      this.setSpacebreaksForCanvas();
      // Step 4: Set current points of distribution (PoD). This will be used to calculate product's delta on fetchCanvasProducts.
      // So it depends on the canvas being loaded.
      this.setSelectedPod(
        this.getPointsOfDistribution[this.selectedCanvas.clusterId][
          this.selectedCanvas.storeClassId
        ]
      );
      // Step 5: Fetch products (depends on PoD being set). This sets product's PoD deltas.
      const productPromises = Promise.all([
        this.fetchScenarioProducts({ params: spParams }),
        this.fetchCanvasProducts(canvasId),
      ]);
      // after pod was loaded, fetch products with deltas
      const [scenarioProducts] = await productPromises;
      // Step 6: Update state with scenario products. This needs to be run after fetch scenario products as it does not update state
      this.setScenarioProducts(scenarioProducts);
      // Step 7: Restore the scenario-level list column preferences for the loaded canvas.
      this.initializeListViewColumns();
      // Step 8: Clean up UI
      this.closeSpacebreakSettingsPanel();
      this.stopRenderingScreen();
    },

    // TODO: ensure user always has referenceCheckpoint. Otherwise error is thrown
    // webtool/client/js/store/modules/assortment-canvas.js:323
    // Cannot read property '_id' of undefined
    async closeCheckpoints() {
      // We re-fetch reference checkpoints on close only if the reference has changed from the current selection
      const oldReferenceCheckpointId = get(this.referenceCheckpoint, '_id', null);
      const newReferenceCheckpointId = this.selectedReferenceCheckpoint;
      const referenceCheckpointHasChanged = newReferenceCheckpointId !== oldReferenceCheckpointId;

      if (referenceCheckpointHasChanged) {
        await this.setReferenceCheckpoint({ oldReferenceCheckpointId, newReferenceCheckpointId });
        await this.refreshReferenceCheckpointData();
        this.refreshProductDeltas();
      }

      this.isCheckpointListModalOpen = false;
    },

    setSpacebreaksForCanvas() {
      // This gives us the spacebreaks info on assortment canvas store
      // In the future, this will be returned in a single initial call so will read from that rather than the scen-furn store.
      const storeClass = find(this.scenarioFurniture.storeClasses, {
        _id: this.selectedCanvas.storeClassId,
      });
      const spacebreaks = storeClass ? storeClass.spacebreaks : [];
      // sorting is crucial here to ensure nesting and cumulative figures are correct
      this.setSpacebreaks(sortBy(spacebreaks, 'fillOverride'));
    },

    getCdtLeafs(cdts, cannGroupId, parentId = null) {
      const parents = filter(cdts, cdt => cdt.parentId === parentId);

      each(parents, p => {
        this.cdtCols.push({
          cdtId: p._id,
          cdtValue: [...(p.attributeValue || [])],
          cannGroupId,
        });
      });
    },

    getCdtCols() {
      each(this.scenarioCdts, scenarioCdt => {
        this.getCdtLeafs(scenarioCdt.cdt, scenarioCdt.cannGroupId);
      });
    },

    getCdtOrder(cannGroupId) {
      if (!this.visibleCanvasCannGroups) return null;

      const matchingCannGroup = find(this.visibleCanvasCannGroups, { key: cannGroupId });
      return matchingCannGroup ? matchingCannGroup.order : null;
    },

    async displayCheckpoints() {
      // if we navigate through toolbar we skip assortments panel component which fetches canvases
      // therefore we need to fetch it here in order to update checkpoints
      if (isEmpty(this.canvases)) await this.fetchCanvases();
      await this.fetchCheckpoints();
      this.isCheckpointListModalOpen = true;
    },

    // Check if a checkpoint should be created before kicking off optimisation
    // First check if anything has changed vs the previous checkpoint (function returns true if there are changes in data)
    // If it has changes since last checkpoint, then ask the user if they want to create a checkpoint
    // If no changes, then just run the optimiser
    async isCheckpointCreationEligible() {
      const showCheckpointCreationModal = await jobApi.runFunction('checkpoint-creation-eligible', {
        canvas_id: this.selectedCanvas._id,
      });
      if (showCheckpointCreationModal) {
        this.isConfirmationCheckpointCreationModalOpen = true;
      } else {
        await this.initOptimisation(false);
      }
    },

    async initOptimisation(createCheckpoint) {
      const payload = {
        scenarioId: this.selectedScenario._id,
        canvasIds: [this.selectedCanvas._id],
        useFurniture: true,
        createCheckpoint,
      };
      const err = await this.runAssortmentOptimisation(payload);
      await this.fetchCanvases();
      if (isEmpty(err)) {
        this.setAssortmentCanvasProducts([]);
      } else {
        this.stopRenderingScreen();
      }
    },

    stopRenderingScreen() {
      this.$nextTick(() => this.changeCanvasRenderingStatus(false));
    },

    async updateSelectedView(v) {
      // loading canvas products again here is more performant than replacing existing data in the grid
      // we replace data with [], set new view state, render progress bar,
      // then Vue destroys rendered grid component and starts rendering new one leaving the progress bar in place meanwhile
      this.setAssortmentCanvasProducts([]);
      this.setSelectedView(v);
      // reset any possible sidepanel selection
      this.setIsProductSidebarOpen(false);
      this.setDashboardProduct(null);
      // making this a local data flag, as updating the store state is too expensive
      this.renderingCanvas = true;
      await this.fetchCanvasProducts(this.selectedCanvas._id);
      this.renderingCanvas = false;
    },

    selectDisplayByMenuItem(item) {
      this.setDisplayBy(item);
      this.displayByMenu = item;
    },

    // ─── AI Panel ────────────────────────────────────────────────────────────────

    /**
     * Build and push the current AI side panel context based on the active sub-view.
     */
    buildInnerContext() {
      const section = this.currentAiSection;

      const optimiserStatus = this.isAnyJobRunning
        ? 'An optimisation job is currently running. Products are being re-ranked — avoid manual edits until complete.'
        : 'No job running — canvas is ready for review and editing.';

      const contextDisplay = [this.selectedScenario?.name, this.buildCanvasLabel()].filter(Boolean);

      const isTileOrList = section === 'assortment-tile' || section === 'assortment-list';

      const context = {
        section,
        contextDisplay,
        context: {
          scenarioName: this.selectedScenario?.name || 'Unknown',
          workpackageName: this.selectedWorkpackage?.name || 'Unknown',
          canvasInfo: this.buildCanvasInfo(),
          optimiserStatus,
          spacebreaksInfo: this.buildSpacebreaksInfo(),
          availableCanvases: this.buildAvailableCanvases(),
          activeFilterCount: (this.canvasProductsFilters || []).length,
          // Products with open tooltips (tile/list only, max 5)
          openedProducts:
            isTileOrList && this.openedProductTooltips.length > 0
              ? this.buildOpenedProductsContext()
              : undefined,
          selectedSpacebreakInfo:
            section === 'assortment-constraints' ? this.buildConstraintsInfo() : undefined,
          // Rich single-product context for the product dashboard
          dashboardProduct:
            section === 'assortment-product-dashboard'
              ? this.buildDashboardProductContext()
              : undefined,
        },
      };

      return context.context;
    },

    /**
     * Short label for the current canvas used in contextDisplay.
     */
    buildCanvasLabel() {
      const cluster =
        get(this.selectedCanvas, 'clusterName') ||
        get(this.selectedCanvas, 'clusterId') ||
        'Unclustered';
      const storeClass =
        get(this.selectedCanvas, 'storeClassName') ||
        get(this.selectedCanvas, 'storeClassId') ||
        'Unknown';
      return `${cluster} / ${storeClass}`;
    },

    /**
     * Format a constraint's [min, max] limits array into a readable string.
     * limits is [minValue|null, maxValue|null]
     */
    formatConstraintLimits(limits) {
      if (!Array.isArray(limits)) return '';
      const [min, max] = limits;
      if (min !== null && max !== null) return `min ${min} / max ${max}`;
      if (min !== null) return `min ${min}`;
      if (max !== null) return `max ${max}`;
      return '';
    },

    /**
     * Build canvas-level context (cluster, store class, status).
     */
    buildCanvasInfo() {
      if (!this.selectedCanvas._id) return null;
      const cluster =
        get(this.selectedCanvas, 'clusterName') ||
        get(this.selectedCanvas, 'clusterId') ||
        'Unclustered';
      const storeClass =
        get(this.selectedCanvas, 'storeClassName') ||
        get(this.selectedCanvas, 'storeClassId') ||
        'Unknown';
      return {
        cluster,
        storeClass,
        view: this.selectedView === this.views.List ? 'List' : 'Tile',
        isOutdated: !!this.selectedCanvas.outdated,
      };
    },

    /**
     * Build per-spacebreak summary: name, product count, space used, constraint count, locked state.
     */
    buildSpacebreaksInfo() {
      if (!this.spacebreaks || this.spacebreaks.length === 0) return [];
      const definitions = get(this.selectedCanvas, 'constraints.definitions', []);
      const canvasConstraints = Array.isArray(definitions) ? definitions : [];

      return this.spacebreaks.map((sb, index) => {
        const name = sb.name || `Spacebreak ${index + 1}`;
        const productsInSb = this.productsPerSpacebreak?.[sb._id];
        const sbConstraints = canvasConstraints.filter(
          c => c.spacebreakId === null || String(c.spacebreakId) === String(sb._id)
        );
        return {
          name,
          productCount: productsInSb ? productsInSb.length : null,
          spaceUsed: this.spaceUsedPerSpacebreak?.[sb._id] ?? null,
          threshold: sb.fillOverride ?? null,
          constraintCount: sbConstraints.length,
          isLocked: !!sb.isLocked,
        };
      });
    },

    /**
     * Build full checkpoint list including forecast status and reference/final markers.
     */
    buildCheckpointsInfo() {
      const allCheckpoints = this.checkpoints || [];
      if (allCheckpoints.length === 0) return [];

      const referenceId = get(this.referenceCheckpoint, '_id');
      return allCheckpoints.map((cp, index) => {
        const forecastStart = get(cp, 'forecastParameters.forecastStart');
        const forecastEnd = get(cp, 'forecastParameters.forecastEnd');
        const forecastedSales = get(cp, 'forecastResults.forecastedSales');
        return {
          name: get(cp, 'checkpoint.name') || `Checkpoint ${index + 1}`,
          createdDate: get(cp, 'checkpoint.createdDate') || null,
          isObservedBaseline: !!cp.isObserved,
          isReference: cp._id === referenceId,
          isFinal: !!cp.isFinalCheckpoint,
          forecast: forecastStart
            ? {
                start: forecastStart,
                end: forecastEnd || null,
                forecastedSales: forecastedSales ?? null,
              }
            : null,
        };
      });
    },

    /**
     * Build context for the active spacebreak's constraints in the constraints editor.
     */
    buildConstraintsInfo() {
      const spacebreak = this.spacebreakSettings.spacebreak;
      if (!spacebreak) return null;

      const rawDefinitions = get(this.selectedCanvas, 'constraints.definitions', []);
      const canvasConstraints = Array.isArray(rawDefinitions) ? rawDefinitions : [];
      const globalConstraints = canvasConstraints.filter(c => c.spacebreakId === null);
      const sbConstraints = canvasConstraints.filter(
        c => c.spacebreakId !== null && String(c.spacebreakId) === String(spacebreak._id)
      );

      const formatConstraint = c => ({
        attributeName: (() => {
          const attrId = get(c, 'attributeFilters.attributeId', '');
          return (attrId && this.scenarioCustomAttributesById[attrId]?.name) || attrId || null;
        })(),
        attributeValues: get(c, 'attributeFilters.attributeValues', []),
        limits: c.limits,
        type: c.type || null,
      });

      return {
        spacebreakName: spacebreak.name || 'Selected Spacebreak',
        spaceThreshold: spacebreak.fillOverride ?? null,
        isLocked: !!spacebreak.isLocked,
        globalConstraints: globalConstraints.map(formatConstraint),
        spacebreakConstraints: sbConstraints.map(formatConstraint),
      };
    },

    /**
     * Build available canvases list for context and switch-canvas supplementary.
     */
    buildAvailableCanvases() {
      return (this.canvases || []).map(c => ({
        id: c._id,
        label: `${c.clusterName || 'Unclustered'} - ${c.storeClassName || 'Unknown'}`,
        isActive: c._id === this.selectedCanvas._id,
        hasBeenOptimised: !!c.hasBeenOptimised,
        isOutdated: !!c.outdated,
      }));
    },

    /**
     * Supplementary context for switch-canvas: formatted list of canvases with ids.
     */
    buildAvailableCanvasesSupplementary() {
      const list = this.buildAvailableCanvases();
      if (list.length === 0) return 'No canvases available.';
      return list
        .map(
          c =>
            `${c.isActive ? '(active) ' : ''}${c.label} — id: ${c.id}${c.hasBeenOptimised ? '' : ' (not yet optimised)'}${c.isOutdated ? ' [outdated]' : ''}`
        )
        .join('\n');
    },

    /**
     * Build context for products with open tooltips (tile/list sections only, max 5).
     */
    buildOpenedProductsContext() {
      const spacebreaks = this.spacebreaks || [];
      return (this.openedProductTooltips || []).slice(0, 5).map(product => {
        const currentSb = spacebreaks.find(sb => sb._id === product.currentSpacebreakId);
        const optimisedSb = spacebreaks.find(sb => sb._id === product.optimisedSpacebreakId);
        const tags = [];
        if (product.isNew) tags.push('NEW — no history, fully modelled');
        else if (product.isModelled) tags.push('MDL — limited data, modelled');
        else if (product.isNewInCluster) tags.push('NIC — new in this cluster');
        return {
          name: product.itemDescription || product.productKeyDisplay,
          productKey: product.productKeyDisplay || product.productKey,
          content: product.contentValue
            ? `${product.contentValue} ${product.contentUnitOfMeasure || ''}`.trim()
            : null,
          size: product.size ?? null,
          tag: tags[0] || null,
          currentSpacebreak: currentSb?.name || product.currentSpacebreakId || 'Not listed',
          optimisedSpacebreak: optimisedSb?.name || product.optimisedSpacebreakId || 'Not listed',
          lockState: product.lockType ?? null,
          rank: product.optimisedRank ?? null,
          sales: product.sales ?? null,
          volume: product.volume ?? null,
          margin: product.margin ?? null,
          selfSwitching: product.selfSwitching ?? null,
          optimiserNotes: product.optimisedNote || null,
        };
      });
    },

    /**
     * Build rich single-product context for the product dashboard section.
     * Includes KPIs, switching partners, transferred spend, and distribution.
     */
    buildDashboardProductContext() {
      const product = this.getDashboardProduct;
      if (!product) return null;

      const spacebreaks = this.spacebreaks || [];
      const currentSb = spacebreaks.find(sb => sb._id === product.currentSpacebreakId);
      const optimisedSb = spacebreaks.find(sb => sb._id === product.optimisedSpacebreakId);

      const tags = [];
      if (product.isNew) tags.push('NEW — no history, fully modelled');
      else if (product.isModelled) tags.push('MDL — limited data, modelled');
      else if (product.isNewInCluster) tags.push('NIC — new in this cluster');

      // Top switching partners (enriched with product names from canvas)
      const allCanvasProducts = this.canvasProducts || [];
      const topPartners = (this.getSelectedProductTopPartners || []).slice(0, 5).map(tp => {
        const partner = allCanvasProducts.find(p => String(p.productKey) === String(tp.to));
        return {
          name: partner?.itemDescription || partner?.productKeyDisplay || String(tp.to),
          switchingProbability: tp.value ?? null,
        };
      });

      // Transferred spend flows
      const ts = this.getSelectedProductTransferredSpend || {};
      const hasSpendData = Object.keys(ts).length > 0;
      const transferredSpend = hasSpendData
        ? {
            salesLost: get(ts, 'transferredSalesFrom.salesLost', null),
            salesLostPercentage: get(ts, 'transferredSalesFrom.salesLostPercentage', null),
            newSales: get(ts, 'transferredSalesTo.newSales', null),
            topOutbound: get(ts, 'transferredSalesFrom.top', [])
              .slice(0, 3)
              .map(tp => {
                const p = allCanvasProducts.find(
                  cp => String(cp.productKey) === String(tp.productKeyTo)
                );
                return {
                  name: p?.itemDescription || String(tp.productKeyTo),
                  amount: tp.amount,
                  percentage: tp.percentage,
                };
              }),
            topInbound: get(ts, 'transferredSalesTo.top', [])
              .slice(0, 3)
              .map(tp => {
                const p = allCanvasProducts.find(
                  cp => String(cp.productKey) === String(tp.productKeyFrom)
                );
                return {
                  name: p?.itemDescription || String(tp.productKeyFrom),
                  amount: tp.amount,
                  percentage: tp.percentage,
                };
              }),
          }
        : null;

      return {
        name: product.itemDescription || product.productKeyDisplay,
        productKey: product.productKeyDisplay || product.productKey,
        tags,
        content: product.contentValue
          ? `${product.contentValue} ${product.contentUnitOfMeasure || ''}`.trim()
          : null,
        currentSpacebreak: currentSb?.name || product.currentSpacebreakId || 'Not listed',
        optimisedSpacebreak: optimisedSb?.name || product.optimisedSpacebreakId || 'Not listed',
        lockState: product.lockType ?? null,
        rank: product.optimisedRank ?? null,
        sales: product.sales ?? null,
        volume: product.volume ?? null,
        margin: product.margin ?? null,
        selfSwitching: product.selfSwitching ?? null,
        forecastedSales: product.forecastedSales ?? null,
        forecastedVolume: product.forecastedVolume ?? null,
        forecastedMargin: product.forecastedMargin ?? null,
        optimiserNotes: product.optimisedNote || null,
        topSwitchingPartners:
          topPartners.length > 0
            ? topPartners
            : 'Switching partner data not yet loaded — it loads when the dashboard opens.',
        transferredSpend:
          transferredSpend ||
          'Transferred spend data not yet loaded — it loads when the dashboard opens.',
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.main-container-canvas {
  height: 100%;
  padding-bottom: 3rem; // Ensures the footer does not overlap the Canvas.
}

.infinite-canvas {
  width: 100%; // this gives a fixed width, we need this for scroll
  height: 100px; // this height is misleading, it is actually dynamic due to the flex-grow property, but we need this fixed height
  flex-direction: column;
  display: flex;
  overflow: auto;
}

.canvas-wrapper {
  height: 100px;
  display: flex;
  overflow: auto;
  flex-grow: 1;
}

.grid-container-wrapper {
  flex-grow: 1;
  overflow: auto;
}

.grid-container-overflow {
  position: relative;
}

.docs-link {
  margin-bottom: 2px;
}

.toolbar-row {
  :deep(.v-btn-group--density-default.v-btn-group) {
    height: 25px !important;
  }

  :deep(.v-btn-group--density-default .v-btn) {
    height: 25px !important;
    border-radius: 0 !important;
  }

  :deep(.v-btn__content) {
    height: 25px !important;
  }
}

.warning-banner {
  font-size: 1.5rem;
  color: $assortment-warning-alert-border;
}

.display-by-title {
  font-size: 12px;
}

.checkpoints-dialog {
  :deep(.base-dialog-card__footer) {
    display: none;
  }

  :deep(.base-dialog-card__header) {
    .v-row {
      align-items: center;
    }
  }
}

.checkpoint-creation-dialog {
  :deep(.base-dialog-card__header) {
    .v-row {
      align-items: center;
    }
  }
}
</style>
